import { ArrowRightOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { Sparkles } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import './QuickActionChip.css';

/**
 * QuickActionChip - Universal chip for initiating AI conversations from anywhere in the app
 * 
 * Always navigates to new chat page (/) with context, always uses Sparkles icon.
 * The context is passed as query params and used to pre-fill the chat prompt.
 */
const QuickActionChip = ({ 
  label, 
  action,
  context = {},
  size = 'medium',
  className = '',
  disabled = false,
  onClick
}) => {
  const navigate = useNavigate();

  const handleClick = (e) => {
    if (disabled) return;
    
    // Call custom onClick handler if provided
    if (onClick) {
      onClick(e);
    }
    
    // Build query params from action and context
    const params = new URLSearchParams({ action });
    
    // Add all context values as query params
    Object.entries(context).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    });
    
    // Navigate to new chat page with params
    navigate(`/?${params.toString()}`);
  };

  const chipClassName = [
    'quick-action-chip',
    `size-${size}`,
    className
  ].filter(Boolean).join(' ');

  return (
    <button
      className={chipClassName}
      onClick={handleClick}
      disabled={disabled}
      type="button"
      aria-label={label}
    >
      <Sparkles size={14} className="quick-action-chip-icon" />
      <span className="quick-action-chip-text">{label}</span>
      <ArrowRightOutlined className="quick-action-chip-arrow" />
    </button>
  );
};

QuickActionChip.propTypes = {
  /** Display text for the chip */
  label: PropTypes.string.isRequired,
  /** Action type that determines which template to load (e.g., 'avatar_use', 'project_analyze') */
  action: PropTypes.string.isRequired,
  /** Context data passed to the template (e.g., { avatar_name: 'Luna' }) */
  context: PropTypes.object,
  /** Size variant */
  size: PropTypes.oneOf(['small', 'medium']),
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Disable the chip */
  disabled: PropTypes.bool,
  /** Additional click handler (navigation still happens) */
  onClick: PropTypes.func,
};

export default QuickActionChip;
